/**
 * About page "More From" — sourced only from `template_components`, limited to
 * rows that qualify for the Recent Content page (Latest + Series tabs).
 *
 * Public rendering intersects with {@link useAboutMoreFromItems} so cards only
 * appear when the matching content tab is visible on `/content`.
 */

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { isNavigable, pickFirstNonEmptyImageField } from "../../../Utils/StayUpToDate/RenderComponent";
import {
  isCarouselLatestTab,
  isCarouselSeriesTab,
  CONTENT_SERIES_COMPONENT_TYPE,
} from "../../../Utils/StayUpToDate/stayUpToDateComponentTabFilters";
import type { CreatorData, TemplateContentView } from "./types";
import { carouselAnalyticsRank } from "../../../Utils/analytics/carouselAnalyticsKey";

dayjs.extend(utc);
dayjs.extend(timezone);

type TemplateComponentRow = {
  id?: string | number;
  name?: string;
  display_name?: string;
  type?: string;
  card_image?: unknown;
  cta_placeholder?: string;
  route_name?: string;
  unique_id?: string;
  status_expiry?: string;
  isEmpty?: boolean;
};

/** Same 7-day window as link-in-bio `AdditionalCarousels`. */
export function getMoreFromCarouselClickDateRange(): {
  startDate: string;
  endDate: string;
} {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return {
    startDate: dayjs().tz(tz).utc().subtract(7, "day").format("YYYY-MM-DD"),
    endDate: dayjs().tz(tz).utc().toISOString(),
  };
}

function buildCarouselClickRankMap(
  carouselClicksAnalytics: Record<string, unknown> | null | undefined,
): Map<string, number> {
  const map = new Map<string, number>();
  if (!carouselClicksAnalytics || typeof carouselClicksAnalytics !== "object") {
    return map;
  }

  const totals: { key: string; totalSum: number }[] = [];
  for (const key of Object.keys(carouselClicksAnalytics)) {
    const items = carouselClicksAnalytics[key];
    let totalSum = 0;
    if (Array.isArray(items)) {
      for (let i = 0; i < items.length; i++) {
        const oneKey = items[i] as {
          count?: number;
          id?: unknown;
          idCount?: number;
        };
        totalSum +=
          (oneKey?.count || 0) +
          (oneKey?.id && oneKey?.idCount ? oneKey.idCount : 0);
      }
    }
    totals.push({ key, totalSum });
  }
  totals.sort((a, b) => b.totalSum - a.totalSum);
  for (let i = 0; i < totals.length; i++) {
    map.set(totals[i].key, i);
  }
  return map;
}

/** Content-page tab rows only — same Latest + Series filters as Recent Content. */
export function isTemplateComponentRecentContentTab(
  component: { type?: string; display_name?: string; name?: string },
): boolean {
  const shape = {
    componentType: component.type,
    componentDisplayName: component.display_name,
    name: component.name,
    title: component.name,
  };
  return isCarouselLatestTab(shape) || isCarouselSeriesTab(shape);
}

function componentRenderSlug(comp: TemplateComponentRow): string {
  return String(
    (comp.route_name && String(comp.route_name).trim()) ||
      (comp.unique_id && String(comp.unique_id).trim()) ||
      (comp.name && String(comp.name).trim()) ||
      "",
  ).trim();
}

function rowIdKey(row: { id?: string | number } | null | undefined): string {
  if (row?.id == null) return "";
  return String(row.id).trim();
}

/** Active + archived carousels may appear in More From (not drafts / hidden). */
function isMoreFromEligibleStatus(statusExpiry: unknown): boolean {
  const status = String(statusExpiry ?? "active")
    .trim()
    .toLowerCase();
  if (!status || status === "active") return true;
  if (status === "archive" || status === "archived") return true;
  return false;
}

function selectRankedTemplateComponents(
  components: TemplateComponentRow[],
  carouselClicksAnalytics: Record<string, unknown> | null | undefined,
  maxCount = 6,
): TemplateComponentRow[] {
  if (!Array.isArray(components) || components.length === 0) return [];

  const rankByName = buildCarouselClickRankMap(carouselClicksAnalytics);

  const prefiltered = components
    .filter((c) => isNavigable(c))
    .filter((elem) => {
      return !elem.isEmpty && isMoreFromEligibleStatus(elem.status_expiry);
    });

  const ranked = prefiltered.map((c) => ({
    comp: c,
    rank: carouselAnalyticsRank(rankByName, c),
  }));

  ranked.sort((a, b) => a.rank - b.rank);
  return ranked.slice(0, maxCount).map((x) => x.comp);
}

/**
 * Build About `values[]` candidates from `template_components` only.
 * Does not fall back to link-in-bio rows.
 */
export function buildAboutMoreFromValuesFromTemplateComponents(
  templateComponents: unknown[],
  carouselClicksAnalytics?: Record<string, unknown> | null,
): CreatorData["about"]["values"] {
  if (!Array.isArray(templateComponents) || templateComponents.length === 0) {
    return [];
  }

  const contentRows = (templateComponents as TemplateComponentRow[]).filter(
    (row) => isTemplateComponentRecentContentTab(row),
  );

  const selected = selectRankedTemplateComponents(
    contentRows,
    carouselClicksAnalytics ?? undefined,
  );

  return selected
    .map((comp) => {
      const image = pickFirstNonEmptyImageField(comp.card_image);
      const title =
        String(comp.name ?? "").trim() ||
        String(comp.display_name ?? "").trim();
      const renderSlug = componentRenderSlug(comp);
      const isSeries =
        (comp.type ?? "").toString().trim() === CONTENT_SERIES_COMPONENT_TYPE;
      const contentView: TemplateContentView = isSeries ? "series" : "latest";
      const rowId = rowIdKey(comp);
      const base = {
        title,
        body: String(comp.cta_placeholder ?? ""),
        image,
        ...(rowId ? { stayComponentRowId: rowId } : {}),
      };
      return renderSlug
        ? {
            ...base,
            renderSlug,
            tabKind: "content" as const,
            contentView,
          }
        : base;
    })
    .filter((v) => v.title);
}
